// Every user drawing, painted by ONE series primitive.
//
// One layer rather than one primitive per shape, for three reasons that all
// bite in practice: v4 caches paneViews() by array reference, so attaching and
// detaching per edit churns that cache on every drag end; one layer gives one
// hit test with correct precedence between overlapping shapes; and one
// autoscaleInfo() instead of N competing ones.
//
// The deliberate inversion from box-primitive.js, which expands autoscale so a
// stop is never off-screen: this returns NULL. A user's trendline anchored 300
// points away must never zoom the chart out from under them -- their own
// drawing would then hide the price they drew it about.

import { hitParts, interpolateY, toleranceFor } from "./drawing-geometry.js";

// A stored timestamp that is not a bar boundary of the CURRENT frame is
// normal, not exceptional: it happens on every timeframe switch. The library
// signals that by throwing in the development build, so one bad anchor must
// not take the whole layer's paint down with it.
function safeTimeToCoordinate(timeScale, time) {
  try {
    return timeScale.timeToCoordinate(time);
  } catch (error) {
    return null;
  }
}

const STYLE = {
  hline:     { color: "#E9C46A", width: 2, dash: [] },
  trendline: { color: "#6E9BFF", width: 2, dash: [] },
  ray:       { color: "#6E9BFF", width: 2, dash: [] },
  rect:      { color: "#7FD1AE", width: 1, dash: [4, 4] },
  fib:       { color: "#C9A227", width: 1, dash: [] },
  trade:     { color: "#E9C46A", width: 1, dash: [] },
  vline:     { color: "#8FA9C4", width: 1, dash: [4, 4] },
  arrow:     { color: "#6E9BFF", width: 2, dash: [] },
  channel:   { color: "#7FD1AE", width: 1, dash: [] },
};

const CHANNEL_FILL = "rgba(127, 209, 174, 0.07)";

const TRADE_RISK = "rgba(180, 99, 90, 0.12)";
const TRADE_REWARD = "rgba(46, 158, 107, 0.12)";

// The retracement set traders actually use. 0 and 1 are the anchors' own
// prices; the golden pocket (0.618) carries the emphasis.
const FIB_RATIOS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];

const SELECTED = "#FFFFFF";
const HANDLE_R = 6;

class DrawingRenderer {
  constructor(source) {
    this._source = source;
  }

  // Drawings paint on the FOREGROUND: a line the user placed themselves is
  // the thing they are looking for, and hiding it under the candles would make
  // it useless exactly when price reaches it.
  draw(target) {
    const source = this._source;
    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hRatio = scope.horizontalPixelRatio;
      const vRatio = scope.verticalPixelRatio;
      const width = scope.bitmapSize.width;

      for (const drawing of source._drawings) {
        const pts = source.anchorsToPixels(drawing);
        if (!pts.length || pts.some((p) => p === null)) continue;
        const style = STYLE[drawing.kind] || STYLE.trendline;
        const selected = source._selectedId === drawing.id;

        ctx.save();
        ctx.strokeStyle = selected ? SELECTED : (drawing.style?.color || style.color);
        ctx.lineWidth = Math.max(1, (drawing.style?.width || style.width) * vRatio);
        ctx.setLineDash((style.dash || []).map((d) => d * hRatio));
        ctx.beginPath();

        if (drawing.kind === "hline") {
          const y = pts[0].y * vRatio;
          ctx.moveTo(0, y);
          ctx.lineTo(width, y);
        } else if (drawing.kind === "vline") {
          // A moment, marked: full-height dashed at the anchor's time.
          const x = pts[0].x * hRatio;
          ctx.moveTo(x, 0);
          ctx.lineTo(x, scope.bitmapSize.height);
        } else if (drawing.kind === "rect") {
          const x1 = pts[0].x * hRatio, y1 = pts[0].y * vRatio;
          const x2 = pts[1].x * hRatio, y2 = pts[1].y * vRatio;
          ctx.rect(Math.min(x1, x2), Math.min(y1, y2),
                   Math.abs(x2 - x1), Math.abs(y2 - y1));
        } else if (drawing.kind === "trade") {
          // The position tool (P14): entry, stop and target as draggable
          // lines with their zones tinted -- risk red, reward green -- and
          // the arithmetic ON the drawing: distances in dollars and the R:R
          // that follows from wherever the three handles sit right now.
          // Visualization only: the canonical sizing stays the verdict's.
          const [e, s, tp] = pts;
          const pE = drawing.anchors?.[0]?.p, pS = drawing.anchors?.[1]?.p,
                pT = drawing.anchors?.[2]?.p;
          if (!e || !s) { ctx.restore(); continue; }
          const left = e.x * hRatio;
          const color = selected ? SELECTED : (drawing.style?.color || style.color);
          const zone = (y1, y2, fill) => {
            ctx.fillStyle = fill;
            ctx.fillRect(left, Math.min(y1, y2), Math.max(0, width - left),
                         Math.abs(y2 - y1));
          };
          zone(e.y * vRatio, s.y * vRatio, TRADE_RISK);
          if (tp) zone(e.y * vRatio, tp.y * vRatio, TRADE_REWARD);
          ctx.font = `${Math.round(10 * vRatio)}px "Inter Tight", system-ui, sans-serif`;
          ctx.textBaseline = "bottom";
          const level = (pt, stroke, label) => {
            if (!pt) return;
            const y = pt.y * vRatio;
            ctx.beginPath();
            ctx.setLineDash([]);
            ctx.lineWidth = Math.max(1, 1 * vRatio);
            ctx.strokeStyle = stroke;
            ctx.moveTo(left, y);
            ctx.lineTo(width, y);
            ctx.stroke();
            ctx.fillStyle = stroke;
            ctx.fillText(label, left + 4 * hRatio, y - 3 * vRatio);
          };
          const risk = (pE != null && pS != null) ? Math.abs(pE - pS) : null;
          const reward = (pE != null && pT != null) ? Math.abs(pT - pE) : null;
          const rr = (risk && reward) ? (reward / risk) : null;
          const long = pT != null && pE != null ? pT > pE : true;
          level(e, color, `${long ? "LONG" : "SHORT"}  ${pE != null ? pE.toFixed(2) : ""}`);
          level(s, "#B4635A", `SL ${pS != null ? pS.toFixed(2) : ""}${risk != null ? `  −${risk.toFixed(2)}` : ""}`);
          if (tp) level(tp, "#2E9E6B",
            `TP ${pT != null ? pT.toFixed(2) : ""}${reward != null ? `  +${reward.toFixed(2)}` : ""}${rr != null ? `  R:R ${rr.toFixed(2)}` : ""}`);
          ctx.beginPath();
        } else if (drawing.kind === "fib") {
          // Levels extend from the earlier anchor to the pane's right edge:
          // a retracement is a forward-looking map, not a picture of the
          // swing it was measured on. The faint diagonal keeps the measured
          // swing visible; each level carries its ratio AND its price, so
          // the drawing answers without a crosshair trip.
          const [a, b] = pts;
          const pA = drawing.anchors?.[0]?.p, pB = drawing.anchors?.[1]?.p;
          const startX = Math.min(a.x, b.x) * hRatio;
          const color = selected ? SELECTED : (drawing.style?.color || style.color);
          ctx.globalAlpha = 0.45;
          ctx.setLineDash([3 * hRatio, 3 * hRatio]);
          ctx.moveTo(a.x * hRatio, a.y * vRatio);
          ctx.lineTo(b.x * hRatio, b.y * vRatio);
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.font = `${Math.round(10 * vRatio)}px "Inter Tight", system-ui, sans-serif`;
          ctx.textBaseline = "bottom";
          for (const ratio of FIB_RATIOS) {
            const y = (a.y + (b.y - a.y) * ratio) * vRatio;
            const golden = ratio === 0.618;
            ctx.beginPath();
            ctx.setLineDash(ratio === 0 || ratio === 1 ? [] : [4 * hRatio, 4 * hRatio]);
            ctx.lineWidth = Math.max(1, (golden ? 2 : 1) * vRatio);
            ctx.strokeStyle = color;
            ctx.moveTo(startX, y);
            ctx.lineTo(width, y);
            ctx.stroke();
            if (pA != null && pB != null) {
              const price = pA + (pB - pA) * ratio;
              ctx.fillStyle = color;
              ctx.fillText(
                `${(ratio * 100).toFixed(1).replace(/\.0$/, "")}%  ${price.toFixed(2)}`,
                startX + 4 * hRatio, y - 3 * vRatio);
            }
          }
          ctx.beginPath();
        } else if (drawing.kind === "channel") {
          // Two parallel rails and the faint corridor between them. The
          // third anchor only sets the OFFSET; the rails share the slope of
          // the first pair, in pixel space, like every platform draws it.
          const [a, b, c] = pts;
          if (!c) { ctx.restore(); continue; }
          const x1 = a.x * hRatio, y1 = a.y * vRatio;
          const x2 = b.x * hRatio, y2 = b.y * vRatio;
          const slope = (x2 - x1) === 0 ? 0 : (y2 - y1) / (x2 - x1);
          const yOnLine = y1 + slope * (c.x * hRatio - x1);
          const dy = c.y * vRatio - yOnLine;
          ctx.fillStyle = CHANNEL_FILL;
          ctx.beginPath();
          ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
          ctx.lineTo(x2, y2 + dy); ctx.lineTo(x1, y1 + dy);
          ctx.closePath(); ctx.fill();
          ctx.beginPath();
          ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
          ctx.moveTo(x1, y1 + dy); ctx.lineTo(x2, y2 + dy);
        } else if (drawing.kind === "arrow") {
          // A pointed statement: the segment plus a head at the second
          // anchor, sized with the zoom so it never becomes a smudge.
          const [a, b] = pts;
          const x1 = a.x * hRatio, y1 = a.y * vRatio;
          const x2 = b.x * hRatio, y2 = b.y * vRatio;
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          const angle = Math.atan2(y2 - y1, x2 - x1);
          const head = 9 * hRatio;
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6),
                     y2 - head * Math.sin(angle - Math.PI / 6));
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6),
                     y2 - head * Math.sin(angle + Math.PI / 6));
        } else {
          let [a, b] = pts;
          if (drawing.kind === "ray") {
            // A ray keeps going: extend to the pane edge in the direction the
            // second anchor points, so it stays useful as new bars arrive.
            const endX = b.x >= a.x ? width / hRatio : 0;
            b = { x: endX, y: interpolateY(endX, a.x, a.y, b.x, b.y) };
          }
          ctx.moveTo(a.x * hRatio, a.y * vRatio);
          ctx.lineTo(b.x * hRatio, b.y * vRatio);
        }
        ctx.stroke();

        // The frame it was drawn on, at the leading edge of the line. A daily
        // level and a five-minute scribble look identical on a five-minute
        // chart, and they should not be read with equal weight.
        if (drawing.timeframe && drawing.kind === "hline") {
          ctx.setLineDash([]);
          ctx.font = `${Math.round(10 * vRatio)}px "Inter Tight", system-ui, sans-serif`;
          ctx.fillStyle = selected ? SELECTED : (drawing.style?.color || style.color);
          ctx.textBaseline = "bottom";
          ctx.fillText(String(drawing.timeframe), 6 * hRatio, pts[0].y * vRatio - 3 * vRatio);
        }

        // Handles only on the selected drawing. Showing them on every line
        // turns a chart with six drawings into a chart of dots.
        if (selected) {
          ctx.setLineDash([]);
          ctx.fillStyle = SELECTED;
          for (const p of pts) {
            ctx.beginPath();
            ctx.arc(p.x * hRatio, p.y * vRatio, HANDLE_R * vRatio, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.restore();
      }
    });
  }
}

class DrawingPaneView {
  constructor(source) {
    this._source = source;
    this._renderer = new DrawingRenderer(source);
  }
  zOrder() { return "top"; }
  update() {}
  renderer() { return this._renderer; }
}

export class DrawingLayer {
  constructor() {
    this._drawings = [];
    this._selectedId = null;
    this._paneViews = [new DrawingPaneView(this)];
    this._series = null;
    this._chart = null;
    this._requestUpdate = null;
  }

  // PriceBox discards requestUpdate; this must keep it, or nothing repaints
  // between the library's own redraws and a drag leaves no trail.
  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews() { return this._paneViews; }
  updateAllViews() { this._paneViews.forEach((v) => v.update()); }

  // Never expand the visible range for a user's own drawing. See the header.
  autoscaleInfo() { return null; }

  repaint() {
    this.updateAllViews();
    if (this._requestUpdate) this._requestUpdate();
  }

  set(drawings) {
    this._drawings = Array.isArray(drawings) ? drawings : [];
    this.repaint();
  }

  select(id) {
    this._selectedId = id;
    this.repaint();
  }

  selected() {
    return this._drawings.find((d) => d.id === this._selectedId) || null;
  }

  // (time, price) -> pixels. Returns null for an anchor the chart cannot
  // place, which happens legitimately: after a timeframe switch a stored
  // timestamp is often not a bar boundary of the new frame.
  anchorsToPixels(drawing) {
    if (!this._series || !this._chart) return [];
    const timeScale = this._chart.timeScale();
    return (drawing.anchors || []).map((anchor) => {
      const y = this._series.priceToCoordinate(anchor.p);
      if (y === null || y === undefined) return null;
      // A horizontal line has no meaningful x; give it one so the shared hit
      // test can treat every kind the same way.
      if (drawing.kind === "hline") return { x: 0, y };
      // An anchor placed in the empty space to the right of the last bar has
      // NO time -- coordinateToTime returns null there, and that null survives
      // the round trip through the server. Asking timeToCoordinate(null) is an
      // assertion failure in the dev build, which threw out of the whole paint
      // and left every drawing invisible. Go straight to the logical fallback.
      let x = anchor.t == null ? null : safeTimeToCoordinate(timeScale, anchor.t);
      if (x === null || x === undefined) {
        // Fall back to the containing bar's logical index. Logical indices are
        // never PERSISTED -- this app prepends history, which would make a
        // stored logical coordinate describe a different bar -- but deriving
        // one at paint time is exactly what keeps a drawing on price across
        // frames.
        //
        // An anchor with neither a usable time nor a session-local index is
        // pinned to the last bar rather than dropped: a drawing that silently
        // disappears is worse than one an inch from where it was placed, and
        // this is the case for a line placed in the right margin then reloaded.
        if (anchor.l == null) {
          const range = timeScale.getVisibleLogicalRange();
          if (!range) return null;
          x = timeScale.logicalToCoordinate(Math.floor(range.to));
          if (x === null || x === undefined) return null;
          return { x, y };
        }
        x = timeScale.logicalToCoordinate(anchor.l);
        if (x === null || x === undefined) return null;
      }
      return { x, y };
    });
  }

  // The drawable width of the price pane, for a ray's extension. Read from
  // the chart element minus the price scale, because a ray is drawn to the
  // pane edge and its hit test has to agree with where it was painted.
  _paneWidth() {
    try {
      const el = this._chart?.chartElement?.();
      const scale = this._chart?.priceScale?.('right')?.width?.() || 0;
      return el ? Math.max(0, el.clientWidth - scale) : 0;
    } catch (error) {
      return 0;
    }
  }

  // Our own hit test, run from pointerdown.
  //
  // The library's hitTest is reached from mouseMoveEvent only -- not from
  // mousedown, click, touchstart or touchmove -- so on a phone
  // param.hoveredObjectId is empty or stale and cannot select anything by
  // finger. Verified in the installed bundle, not assumed from the docs.
  hitTestLocal(x, y, pointerType) {
    const tol = toleranceFor(pointerType);
    // Reverse order: the most recently drawn is on top, so it is hit first.
    for (let i = this._drawings.length - 1; i >= 0; i -= 1) {
      const drawing = this._drawings[i];
      const pts = this.anchorsToPixels(drawing);
      if (!pts.length || pts.some((p) => p === null)) continue;
      const hit = hitParts(drawing.kind, pts, x, y, tol,
                           { paneWidth: this._paneWidth() });
      if (hit) return { ...hit, id: drawing.id, drawing };
    }
    return null;
  }

  // For the desktop cursor only. Kept because a mouse user expects the cursor
  // to change over a grabbable object, and this is the only path that gets it.
  hitTest(x, y) {
    const hit = this.hitTestLocal(x, y, "mouse");
    if (!hit) return null;
    return {
      externalId: hit.id,
      zOrder: "top",
      cursorStyle: hit.part === "handle" ? "grab" : "move",
    };
  }
}
